// src/components/atomic-crm/onboarding/OnboardingWizard.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { useGetIdentity, useNotify, useDataProvider } from "ra-core";
import { Card, CardContent } from "@/components/ds/ui/card";
import { Button } from "@/components/ds/ui/button";
import { Input } from "@/components/ds/ui/input";
import { Label } from "@/components/ds/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ds/ui/select";
import { ContactCompanyImportButton } from "../contacts/ContactCompanyImportButton";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";

type Step = 1 | 2 | 3 | 4;

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { identity, refetch } = useGetIdentity();
  const dataProvider = useDataProvider();

  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [industry, setIndustry] = useState("technology");
  const [teamSize, setTeamSize] = useState("1-5");

  // Step 3 state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("4"); // Default to Sales Rep (id: 4)
  const [inviteSent, setInviteSent] = useState(false);

  const completeOnboarding = async () => {
    if (!identity?.org_id) return;
    setLoading(true);
    try {
      await supabase
        .from("organizations")
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq("id", identity.org_id);

      // 👇 Force a hard reload to clear the React Admin identity cache
      window.location.href = "/";
   //   await refetch(); navigate("/");
    } catch (err: any) {
      notify(err.message || "Failed to complete onboarding", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("users", {
        body: {
          email: inviteEmail.trim(),
          first_name: "Invited",
          last_name: "User",
          role_id: parseInt(inviteRole),
          administrator: inviteRole === "1",
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      notify("Invite sent successfully!", { type: "success" });
      setInviteEmail("");
      setInviteSent(true);
    } catch (err: any) {
      notify(err.message || "Failed to send invite", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl shadow-lg border-muted">
        <CardContent className="p-8 sm:p-10">
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">
                Step {step} of 4
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((s) => (
                  <div
                    key={s}
                    className={`h-1.5 w-8 rounded-full ${
                      s <= step ? "bg-primary" : "bg-muted"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* STEP 1: Business Context */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">
                  Welcome to mOjO
                </h1>
                <p className="text-muted-foreground mt-2">
                  Let's get your pipeline set up in 2 minutes!
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>What industry are you in?</Label>
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">
                        Technology & SaaS
                      </SelectItem>
                      <SelectItem value="realestate">Real Estate</SelectItem>
                      <SelectItem value="manufacturing">
                        Manufacturing
                      </SelectItem>
                      <SelectItem value="consulting">Consulting</SelectItem>
                      <SelectItem value="finance">
                        Financial Services
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>How many people are on your sales team?</Label>
                  <Select value={teamSize} onValueChange={setTeamSize}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select team size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5">Just me (1-5)</SelectItem>
                      <SelectItem value="6-20">Small team (6-20)</SelectItem>
                      <SelectItem value="21-50">Mid-size (21-50)</SelectItem>
                      <SelectItem value="50+">Enterprise (50+)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={() => setStep(2)} className="w-full gap-2">
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* STEP 2: Import Data */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Bring in your data
                </h1>
                <p className="text-muted-foreground mt-2">
                  Import your existing contacts and companies. We'll
                  automatically enrich them with AI profiles.
                </p>
              </div>

              <div className="p-6 border rounded-lg bg-muted/30 flex flex-col items-center justify-center text-center space-y-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Upload a CSV</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto mt-1">
                    Make sure your file includes columns for names, emails, and
                    company names.
                  </p>
                </div>
                <ContactCompanyImportButton />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="w-1/3"
                >
                  Back
                </Button>
                <Button onClick={() => setStep(3)} className="w-2/3 gap-2">
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Invite Team */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Invite your team
                </h1>
                <p className="text-muted-foreground mt-2">
                  Add your sales reps and managers so they can start tracking
                  deals.
                </p>
              </div>

              <div className="space-y-4 p-4 border rounded-lg bg-card">
                <div className="space-y-2">
                  <Label>Email address</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">Sales Rep</SelectItem>
                        <SelectItem value="3">Team Lead</SelectItem>
                        <SelectItem value="2">Sales Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || loading}
                  className="w-full"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Send Invite
                </Button>
                {inviteSent && (
                  <p className="text-sm text-emerald-500 text-center">
                    Invite sent! You can add more or continue.
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="w-1/3"
                >
                  Back
                </Button>
                <Button onClick={() => setStep(4)} className="w-2/3 gap-2">
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 4: The Finish Line */}
          {step === 4 && (
            <div className="space-y-6 text-center animate-in zoom-in-95 duration-500">
              <div className="flex justify-center mb-2">
                <div className="p-4 bg-emerald-500/10 rounded-full">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">
                  You're all set!
                </h1>
                <p className="text-muted-foreground mt-2">
                  Your workspace is ready. We're generating AI profiles for your
                  imported data in the background.
                </p>
              </div>

              <div className="pt-4">
                <Button
                  onClick={completeOnboarding}
                  disabled={loading}
                  className="w-full h-12 text-lg gap-2"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : null}
                  Go to Dashboard <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

OnboardingWizard.path = "/onboarding";
